import React from "react";
import { Link } from "react-router-dom";
import { Heart, Mail, TrendingUp, Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import MarstickLogoMark from "@/components/brand/MarstickLogoMark";
import SocialIcon from "@/components/layout/SocialIcon";
import { footerLinkGroups } from "@/lib/mainNavigation";
import { useLanguage } from "@/lib/LanguageContext.jsx";
import { withLanguagePath } from "@/lib/i18nRoutes";
import { getFooterCopy } from "@/lib/footerLocalization";

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { language } = useLanguage();
  const copy = getFooterCopy(language);

  const footerLinks = footerLinkGroups.map((section) => ({
    ...section,
    category: copy.groups[section.category] || section.category,
    links: section.links.map((link) => ({
      ...link,
      label: copy.groups[link.label] || link.label,
      href: withLanguagePath(link.href, language),
    })),
  }));

  const legalPolicyLinks = [
    { label: "About", href: "/about" },
    { label: "Contact", href: "/contact" },
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Cookie Policy", href: "/cookies" },
    { label: "Terms & Conditions", href: "/terms" },
    { label: "Advertising Policy", href: "/advertising-policy" },
    { label: "Editorial Policy", href: "/editorial-policy" },
    { label: "Disclaimer", href: "/disclaimer" },
    { label: "Safety Disclaimer", href: "/safety-disclaimer" },
  ].map((link) => ({ ...link, href: withLanguagePath(link.href, language) }));

  const sitemapLink = { label: "Sitemap", href: "/sitemap.xml" };

  const socialLinks = [
    { icon: "twitter", label: "X / Twitter", href: "#" },
    { icon: "facebook", label: "Facebook", href: "#" },
    { icon: "instagram", label: "Instagram", href: "#" },
    { icon: "linkedin", label: "LinkedIn", href: "#" }
  ];

  const openCookieSettings = () => {
    document.getElementById("cookie-settings-panel-trigger")?.click();
  };

  return (
    <footer className="bg-card border-t border-border mt-12">
      <div className="max-w-7xl mx-auto px-4 py-12 space-y-8">
        {/* Affiliate CTA Banner */}
        <div className="bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-2xl p-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-5 h-5 text-primary" />
              <h3 className="font-semibold text-foreground">{copy.affiliateTitle}</h3>
            </div>
            <p className="text-sm text-muted-foreground">{copy.affiliateText}</p>
          </div>
          <Link to="/affiliate">
            <Button>
              {copy.joinNow}
            </Button>
          </Link>
        </div>

        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <MarstickLogoMark className="h-9 w-9 rounded-lg" />
              <span className="font-heading font-bold text-lg text-foreground dark:!text-white">Marstick</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {copy.brandText}
            </p>
            <div className="flex gap-3">
              {socialLinks.map(link => (
                <a
                  key={link.label}
                  href={link.href}
                  title={link.label}
                  className="w-9 h-9 rounded-lg bg-muted text-foreground hover:bg-primary hover:text-white dark:bg-white/10 dark:!text-white dark:hover:bg-primary flex items-center justify-center transition-colors"
                >
                  <SocialIcon name={link.icon} />
                </a>
              ))}
            </div>
          </div>

          {/* Links Sections */}
          {footerLinks.map((section) => (
            <div key={section.category} className="space-y-3">
              <h4 className="font-semibold text-sm text-foreground">{section.category}</h4>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter Section */}
        <div className="bg-muted/40 rounded-xl p-6 flex flex-col sm:flex-row items-center gap-4 justify-between">
          <div>
            <h3 className="font-semibold text-sm mb-1">{copy.newsletterTitle}</h3>
            <p className="text-xs text-muted-foreground">{copy.newsletterText}</p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <input
              type="email"
              placeholder={copy.emailPlaceholder}
              className="px-3 py-2 rounded-lg border border-border bg-background text-sm flex-1 sm:flex-none min-w-56"
            />
            <Button size="sm" className="gap-1.5">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">{copy.subscribe}</span>
            </Button>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-border pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <p>© {currentYear} Marstick. {copy.rights}</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {legalPolicyLinks.map((link) => (
              <Link key={link.href} to={link.href} className="transition-colors hover:text-foreground">
                {link.label}
              </Link>
            ))}
            <a href={sitemapLink.href} className="transition-colors hover:text-foreground">
              {sitemapLink.label}
            </a>
            <button
              type="button"
              onClick={openCookieSettings}
              className="inline-flex items-center gap-1 transition-colors hover:text-foreground"
            >
              <Cookie className="w-3 h-3" /> {copy.cookieSettings}
            </button>
            <span className="flex items-center gap-1">{copy.madeWith} <Heart className="w-3 h-3 text-red-500" /> {copy.madeBy}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}